<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { Globe } from 'lucide-vue-next'
import PasswordInput from '@/components/common/PasswordInput.vue'
import LanguageSelector from '@/components/common/LanguageSelector.vue'
import AppLogo from '@/components/common/AppLogo.vue'
import { useConfig } from '@/composables/useConfig'

import { useAuthStore, useI18nStore } from '@99fun-agent/stores'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
const authStore = useAuthStore()
const router = useRouter()
const emit = defineEmits(['login'])

// 获取配置
const { config } = useConfig()

// 获取 i18n
const { t } = useI18n()
const i18nStore = useI18nStore()

const username = ref(authStore.cacheUsername)
const password = ref(authStore.cachePassword)
const rememberMe = ref(authStore.isRemember)
const showLanguages = ref(false)
const usernameInputRef = ref<HTMLInputElement | null>(null)

onMounted(() => {
  // 保持：自动聚焦
  usernameInputRef.value?.focus()
})

const handleSubmit = (e: Event) => {
  e.preventDefault()
  // if (username.value && password.value) {
  //   emit('login', username.value)
  // }

  authStore.reqLogin(
    {
      username: username.value.trim(),
      password: password.value.trim(),
    },
    rememberMe.value,
  )
}

const selectLanguage = async (langCode: string) => {
  // i18nStore.setAppLang(langCode)
  showLanguages.value = false
}

watch(
  () => authStore.isAlreadyLogin,
  (val) => {
    console.log('isAlredy login ', val)
    if (!val) return
    router.push('/dashboard')
  },
  { immediate: true },
)
</script>

<template>
  <div
    class="min-h-screen w-full flex items-center justify-center p-6 bg-gradient-to-br from-[#F2F6FF] via-[#FBF4FF] to-[#FFF2F8] relative font-sans overflow-hidden"
  >
    <div class="absolute top-8 right-8 z-50">
      <button
        @click="showLanguages = !showLanguages"
        class="flex items-center justify-center w-10 h-10 bg-white border border-gray-100 rounded-full shadow-sm hover:shadow-md transition-all active:scale-90"
      >
        <Globe :size="18" class="text-gray-300" />
      </button>

      <div
        v-if="showLanguages"
        class="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-gray-50 py-2 z-[60] animate-in fade-in zoom-in duration-200"
      >
        <LanguageSelector
          @select="selectLanguage($event)"
          :item-class="'w-full text-left px-4 py-2 text-xs hover:bg-gray-50 transition-colors flex items-center gap-2'"
          :selected-item-class="'text-[#8E6DFF] font-bold'"
          :container-class="''"
        >
          <template #item="{ lang, isSelected }">
            <button
              class="w-full text-left flex items-center gap-2"
              :class="isSelected ? 'text-[#8E6DFF] font-bold' : 'text-gray-600'"
            >
              <span class="flex-shrink-0">{{ lang.name }}</span>
              <span class="text-[10px] opacity-70 ml-auto text-right whitespace-nowrap">{{
                lang.nativeName
              }}</span>
            </button>
          </template>
        </LanguageSelector>
      </div>
    </div>

    <div
      class="w-full max-w-[350px] bg-white pt-12 pb-10 px-8 rounded-[110px] shadow-[0_10px_20px_rgba(0,0,0,0.08),0_30px_60px_rgba(0,0,0,0.05)] border border-white flex flex-col items-center relative z-10"
    >
      <!-- 使用 AppLogo 组件 -->
      <AppLogo
        :logo-url="config.logoUrl"
        :logo-text="config.logoText"
        :title="config.title"
        mode="default"
        class="mb-8.5"
      />

      <div class="text-center mb-9.6 space-y-0 flex flex-col items-center w-full px-4">
        <div
          class="bg-gradient-to-r from-[#4A7DFF] via-[#A86DFF] to-[#FF45BF] bg-clip-text text-transparent w-full"
        >
          <h1 class="text-[26px] font-light leading-[1.2] tracking-tight break-words">
            {{ config.title }}
          </h1>
        </div>
        <!-- <p class="text-[13px] text-gray-400 font-medium mt-2">{{ t('login.welcomeBack') }}</p> -->
      </div>

      <form @submit="handleSubmit" class="w-full px-2">
        <div class="space-y-3.5 mb-5">
          <div class="space-y-1">
            <label class="block text-[11px] font-bold text-gray-400 ml-2">{{ t('account') }}</label>
            <input
              ref="usernameInputRef"
              v-model="username"
              type="text"
              :placeholder="t('account_ph')"
              autocomplete="off"
              class="w-full px-5 py-2.5 bg-[#F8FAFF] border border-gray-100 rounded-[12px] text-[13px] focus:bg-white transition-all outline-none placeholder:text-gray-300"
              required
            />
          </div>

          <PasswordInput
            v-model="password"
            :label="t('password')"
            :placeholder="t('password_ph')"
          />
        </div>

        <div
          class="mb-8 flex items-center ml-2 group cursor-pointer"
          @click="rememberMe = !rememberMe"
        >
          <div class="relative flex items-center justify-center w-4 h-4">
            <input
              id="remember"
              :checked="rememberMe"
              type="checkbox"
              class="peer appearance-none w-4 h-4 rounded border border-gray-300 bg-white checked:bg-gray-700 checked:border-gray-700 transition-all cursor-pointer outline-none ring-0 focus:ring-0"
              @click.stop="rememberMe = !rememberMe"
            />
            <svg
              class="absolute w-2.5 h-2.5 text-white pointer-events-none transition-opacity duration-200"
              :class="rememberMe ? 'opacity-100' : 'opacity-0'"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <label
            class="ml-3 text-[11px] font-bold text-gray-400 cursor-pointer select-none group-hover:text-gray-600 transition-colors"
            >{{ t('remember_account') }}</label
          >
        </div>

        <button
          type="submit"
          class="w-full py-3 bg-gradient-to-r from-[#4A7DFF] via-[#A86DFF] to-[#FF45BF] text-white font-black text-[13px] rounded-full shadow-[0_10px_35px_rgba(142,109,255,0.3)] active:scale-[0.96] transition-all duration-300"
        >
          {{ t('login') }}
        </button>
      </form>

      <!-- <div class="mt-4">
        <p class="text-[9px] text-gray-300 font-black tracking-tight uppercase">{{ config.projectName }}</p>
      </div> -->
    </div>
  </div>
</template>

<style scoped>
/* 核心逻辑不碰 */
input:focus,
button:focus,
[type='checkbox']:focus {
  outline: none !important;
  box-shadow: none !important;
}

input:focus {
  border-color: rgba(142, 109, 255, 0.3);
  box-shadow: 0 10px 25px rgba(142, 109, 255, 0.05) !important;
}

/* 隐藏浏览器自带的密码显示按钮 */
input[type='password']::-ms-reveal {
  display: none;
}

input[type='password']::-webkit-credentials-auto-fill-button {
  display: none;
}

/* 描边逻辑不碰 */
.gradient-border-mask {
  background: linear-gradient(135deg, #1e4fd9 0%, #8e6dff 50%, #e65f00 100%);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}
</style>
